<script lang="ts">
  import fragmentShader from './WebGLBackgroundShader.frag';
  import { GLNoise, LFO } from 'block-svelte-webgl';
  import {
    Blur,
    getBGSections,
    getBGHeaderPoints,
    type SectionConfig
  } from './BackgroundGradient.svelte';
  import { onDestroy, onMount } from 'svelte';
  import { TransitionStatus } from '$components/Transitions/PageTransitionStore';
  import WebGlCanvas from '$components/Background/WebGLCanvas.svelte';

  const modules: (string | object)[] = [GLNoise, LFO];
  const speed = 0.1;
  const scale = 1;
  const verticalRange = 0.15;
  let scrollY = $state(0);
  let animated = $state(false);
  interface Props {
    pageHeight?: number;
    sectionConfig?: SectionConfig[];
  }

  let { pageHeight = 1, sectionConfig = [] }: Props = $props();
  let canvas: WebGlCanvas | undefined = $state();

  interface Uniforms {
    blur: number;
    speed: number;
    scale: number;
    verticalRange: number;
    scrollY: number;
    pageHeight?: number;
    numPoints?: number;
    numSections?: number;
    [key: string]: any;
  }

  let uniforms: Uniforms = $state({
    blur: Blur,
    speed,
    scale,
    verticalRange,
    scrollY: 0
  });

  // This is called from webGL render loop, update variable uniforms here
  const updateUniforms = () => {
    //have to counteract the top offset when the modal is open
    uniforms.scrollY = scrollY - (parseInt(document.body.style.top) || 0);
  };

  // unroll bgSections
  if (sectionConfig.length > 0) {
    const bgSections = getBGSections(sectionConfig);
    bgSections.forEach((section, i) => {
      uniforms[`sections[${i}].color`] = section.color;
      //translate to % of page heigth instead of px
      uniforms[`sections[${i}].start`] = section.start / pageHeight;
      uniforms[`sections[${i}].end`] = section.end / pageHeight;
    });
    uniforms.numSections = bgSections.length;

    const bgHeaderPoints = getBGHeaderPoints(sectionConfig);
    bgHeaderPoints.forEach((point, i) => {
      uniforms[`points[${i}].position`] = point.position;
      uniforms[`points[${i}].color`] = point.color;
      uniforms[`points[${i}].seed`] = point.seed;
    });
    uniforms.numPoints = bgHeaderPoints.length;
  }

  if (pageHeight > 1) {
    uniforms.pageHeight = pageHeight;
  }

  onMount(() => {
    animated = true;
    canvas?.init();
  });

  onDestroy(() => {
    animated = false;
    canvas?.cleanup();
  });
</script>

<svelte:window bind:scrollY />
<WebGlCanvas
  bind:this={canvas}
  animated={animated && !$TransitionStatus.isTransitioning}
  {fragmentShader}
  {modules}
  {uniforms}
  {updateUniforms}
/>
